// Presentation Mode's link to the GM's tab.
//
// The table screen is opened with `noopener` so it runs on its own thread. With an opener,
// same-origin windows share one event loop, so a busy table screen (an IR frame streaming
// dozens of touch contacts) froze the GM's tab along with it. Severing the opener also
// stops the browser copying sessionStorage - where the login lives - into the new window,
// so the session is handed over here instead, and the GM toolbar follows the window by
// heartbeat rather than by a window handle.

export const PRESENTATION_PATH = '/tabletop/presentation';
const CHANNEL_NAME = 'dragondrop-presentation';
const HANDOFF_PARAM = 'handoff';
const ALIVE_MS = 1000;
const STALE_MS = 2500;
// How long the GM toolbar shows the window as open before its first heartbeat (page and
// chunk load, then the session handoff reload).
const OPENING_GRACE_MS = 10000;

function openChannel() {
    try { return new BroadcastChannel(CHANNEL_NAME); } catch { return null; }
}

// ── GM's tab ─────────────────────────────────────────────────────────────────
// Module-level so the toolbar can unmount (route nav) and remount without losing track.

let gmChannel; // undefined = not yet opened, null = BroadcastChannel unsupported
let lastAlive = 0;
let openedAt = 0;
let pendingNonce = null;
let fallbackWin = null; // no BroadcastChannel: the old opener-handle behaviour
const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

function ensureGmChannel() {
    if (gmChannel !== undefined) return gmChannel;
    gmChannel = openChannel();
    if (gmChannel) {
        gmChannel.onmessage = (e) => {
            const msg = e.data || {};
            if (msg.type === 'need-session' && msg.nonce && msg.nonce === pendingNonce) {
                // Only the tab that opened this window answers (the nonce is in its URL), so a
                // second tab signed in as someone else can't hand over the wrong session.
                pendingNonce = null;
                const session = {};
                for (let i = 0; i < sessionStorage.length; i++) {
                    const k = sessionStorage.key(i);
                    session[k] = sessionStorage.getItem(k);
                }
                gmChannel.postMessage({type: 'session', nonce: msg.nonce, session});
            } else if (msg.type === 'who') {
                // A table window asking whether a GM tab shares its browser (see
                // startPresentationHeartbeat's onGmHere).
                gmChannel.postMessage({type: 'gm-here'});
            } else if (msg.type === 'alive') {
                lastAlive = Date.now();
                notify();
            } else if (msg.type === 'closed') {
                lastAlive = 0;
                openedAt = 0;
                notify();
            }
        };
    }
    return gmChannel;
}

export function isPresentationOpen() {
    if (gmChannel === null) return !!fallbackWin && !fallbackWin.closed;
    const now = Date.now();
    return now - lastAlive < STALE_MS || now - openedAt < OPENING_GRACE_MS;
}

// Calls `fn` whenever the window's open state may have changed. Also asks any already-open
// table window to say so, so a reloaded GM tab picks it up.
export function subscribePresentation(fn) {
    const ch = ensureGmChannel();
    listeners.add(fn);
    ch?.postMessage({type: 'ping'});
    return () => listeners.delete(fn);
}

export function openPresentation() {
    const ch = ensureGmChannel();
    if (!ch) {
        fallbackWin = window.open(PRESENTATION_PATH, 'dragondrop-presentation');
        notify();
        return;
    }
    pendingNonce = (window.crypto?.randomUUID?.() || String(Math.random()).slice(2));
    openedAt = Date.now();
    window.open(`${PRESENTATION_PATH}?${HANDOFF_PARAM}=${encodeURIComponent(pendingNonce)}`, '_blank', 'noopener');
    notify();
}

export function closePresentation() {
    if (gmChannel === null) {
        fallbackWin?.close();
        fallbackWin = null;
    } else {
        gmChannel?.postMessage({type: 'close'});
        lastAlive = 0;
        openedAt = 0;
    }
    notify();
}

// ── Table window ─────────────────────────────────────────────────────────────

const handoffNonce = () => new URLSearchParams(window.location.search).get(HANDOFF_PARAM);

function stripHandoffParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_PARAM)) return;
    url.searchParams.delete(HANDOFF_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

// A table screen in another browser (see startPresentationHeartbeat) signs in itself: this
// remembers where to come back to, and LoginForm returns there after login.
const RETURN_KEY = 'postLoginReturn';

export function signInAndReturn() {
    try { sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search); } catch { /* private mode */ }
    window.location.assign('/');
}

// The page to go to after login instead of the default, once. Only this app's table screen.
export function takeLoginReturn() {
    let target = null;
    try {
        target = sessionStorage.getItem(RETURN_KEY);
        sessionStorage.removeItem(RETURN_KEY);
    } catch { /* private mode */ }
    return target && target.startsWith(PRESENTATION_PATH) ? target : null;
}

// The table screen's address for opening in another browser, pinned to a campaign so it
// doesn't depend on what that browser has cached.
export function tableScreenLink(campaignId) {
    return `${window.location.origin}${PRESENTATION_PATH}${campaignId ? `?campaign=${encodeURIComponent(campaignId)}` : ''}`;
}

// True when this window was just opened by the GM's tab and hasn't got its session yet.
// A reload of an already-handed-over window keeps its sessionStorage, so it never asks again.
export function needsSessionHandoff() {
    if (sessionStorage.getItem('user')) { stripHandoffParam(); return false; }
    return !!handoffNonce();
}

let handoffPromise = null;

// Asks the opening tab for its sessionStorage and copies it in. Resolves true once it has,
// false if no answer came (that tab closed, or signed out).
export function requestSessionHandoff(timeoutMs = 5000) {
    if (handoffPromise) return handoffPromise;
    handoffPromise = new Promise(resolve => {
        const nonce = handoffNonce();
        const ch = openChannel();
        if (!ch || !nonce) { stripHandoffParam(); resolve(false); return; }
        const finish = (ok) => {
            clearInterval(retry);
            clearTimeout(timer);
            ch.close();
            stripHandoffParam();
            resolve(ok);
        };
        ch.onmessage = (e) => {
            const msg = e.data || {};
            if (msg.type !== 'session' || msg.nonce !== nonce || !msg.session) return;
            Object.entries(msg.session).forEach(([k, v]) => sessionStorage.setItem(k, v));
            finish(!!sessionStorage.getItem('user'));
        };
        const ask = () => ch.postMessage({type: 'need-session', nonce});
        ask();
        const retry = setInterval(ask, 500);
        const timer = setTimeout(() => finish(false), timeoutMs);
    });
    return handoffPromise;
}

// Heartbeat from the table window, so the GM toolbar knows it's open; also closes it on the
// toolbar's request. Returns a cleanup function.
//
// `onGmHere` is called whenever a GM tab answers from this same browser. That matters for
// miniatures: Chrome on Windows drops mouse clicks in *all* its windows within 500ms of any
// touch input (HWNDMessageHandler::IsSynthesizedMouseMessage keys off one timestamp shared
// by every window), and minis resting on an IR frame send touch updates nonstop - so a GM
// tab in the table's browser stops taking clicks. A different browser is a different
// process with its own timestamp; a different profile of the same Chrome is not.
export function startPresentationHeartbeat({onGmHere} = {}) {
    const ch = openChannel();
    if (!ch) return () => {};
    const alive = () => ch.postMessage({type: 'alive'});
    const who = () => ch.postMessage({type: 'who'});
    ch.onmessage = (e) => {
        const type = e.data?.type;
        if (type === 'ping') { alive(); onGmHere?.(); }
        else if (type === 'gm-here') onGmHere?.();
        else if (type === 'close') window.close();
    };
    alive();
    who();
    const id = setInterval(() => { alive(); who(); }, ALIVE_MS);
    const bye = () => ch.postMessage({type: 'closed'});
    window.addEventListener('pagehide', bye);
    return () => {
        clearInterval(id);
        window.removeEventListener('pagehide', bye);
        ch.close();
    };
}
